// El informe visual pintado con el diseño de Venuz (gramática en ./bloques.ts). Markdown sin HTML crudo (react-markdown no
// lo interpreta sin rehype-raw): lo que escribe un agente nunca ejecuta nada en el panel.
import type { Element, ElementContent } from 'hast';
import { AlertTriangle, CheckCircle2, Info, Lightbulb, MessageCircleQuestion } from 'lucide-react';
import type { ReactNode } from 'react';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { MiniaturaImagen } from '../chat/VisorImagen';
import { leerCifras, leerGrafica, leerOpciones, type Aviso } from './bloques';
import { Grafica } from './Grafica';

const AVISO: Record<Aviso, { clave: TKey; icono: typeof Info; clase: string; tinta: string }> = {
  recomendacion: { clave: 'oficinaV2.informe.recomendacion', icono: Lightbulb, clase: 'border-oficina-acento/60 bg-oficina-acento/[0.08]', tinta: 'text-oficina-acento' },
  decision: { clave: 'oficinaV2.informe.decision', icono: MessageCircleQuestion, clase: 'border-oficina-situacion-bloqueado/60 bg-oficina-situacion-bloqueado/[0.08]', tinta: 'text-oficina-situacion-bloqueado' },
  riesgo: { clave: 'oficinaV2.informe.riesgo', icono: AlertTriangle, clase: 'border-oficina-bloqueado/60 bg-oficina-bloqueado/[0.08]', tinta: 'text-oficina-bloqueado' },
  bien: { clave: 'oficinaV2.informe.bien', icono: CheckCircle2, clase: 'border-oficina-trabajando/60 bg-oficina-trabajando/[0.08]', tinta: 'text-oficina-trabajando' },
  nota: { clave: 'oficinaV2.informe.nota', icono: Info, clase: 'border-oficina-linea bg-oficina-panel', tinta: 'text-oficina-texto2' },
};

function texto(nodos: ElementContent[]): string {
  return nodos.map((n) => (n.type === 'text' ? n.value : n.type === 'element' ? texto(n.children) : '')).join('');
}

function lenguaje(code: Element): string | null {
  const cls = code.properties?.className;
  const lista = Array.isArray(cls) ? cls.map(String) : typeof cls === 'string' ? [cls] : [];
  const l = lista.find((c) => c.startsWith('language-'));
  return l ? l.slice('language-'.length) : null;
}

function Cifras({ fuente }: { fuente: string }) {
  const cifras = leerCifras(fuente);
  return (
    <div className="my-6 grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${cifras.length > 3 ? 160 : 200}px, 1fr))` }}>
      {cifras.map((c, i) => (
        <div key={i} className="rounded-o-l border border-oficina-linea bg-oficina-panel px-5 py-4">
          <div className={cn('text-o-titulo font-semibold leading-tight tabular-nums tracking-tight', c.tono === 'bien' ? 'text-oficina-trabajando' : c.tono === 'mal' ? 'text-oficina-error' : 'text-oficina-texto')}>{c.valor}</div>
          <div className="mt-1 text-o-texto font-medium text-oficina-texto2">{c.etiqueta}</div>
          {c.nota ? <div className="mt-0.5 text-o-sub text-oficina-texto3">{c.nota}</div> : null}
        </div>
      ))}
    </div>
  );
}

function Opciones({ fuente, imagen }: { fuente: string; imagen: (src: string) => string | null }) {
  const opciones = leerOpciones(fuente);
  return (
    <div className="my-6 grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))` }}>
      {opciones.map((o, i) => (
        <div key={i} className={cn('rounded-o-l border px-5 py-4', o.recomendada ? 'border-oficina-acento bg-oficina-acento/[0.07]' : 'border-oficina-linea bg-oficina-panel')}>
          {o.recomendada ? <div className="mb-1 text-o-mini font-bold text-oficina-acento">{t('oficinaV2.informe.recomendada')}</div> : null}
          <div className="text-o-cuerpo font-semibold text-oficina-texto">{o.titulo}</div>
          {o.cuerpo ? <div className="mt-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p]:my-1.5 [&_p]:text-o-cuerpo">{md(o.cuerpo, imagen)}</div> : null}
        </div>
      ))}
    </div>
  );
}

function componentes(imagen: (src: string) => string | null): Components {
  return {
    h1: ({ children }) => <h1 className="mb-3 text-o-cifra font-semibold leading-[1.15] tracking-[-0.02em] text-oficina-texto">{children}</h1>,
    h2: ({ children }) => <h2 className="mb-3 mt-10 border-t border-oficina-linea pt-6 text-o-titulo2 font-semibold tracking-[-0.01em] text-oficina-texto">{children}</h2>,
    h3: ({ children }) => <h3 className="mb-2 mt-6 text-o-titulo3 font-semibold text-oficina-texto">{children}</h3>,
    p: ({ children }) => <p className="my-3 text-o-cuerpo leading-[1.65] text-oficina-texto2 [&_strong]:text-oficina-texto">{children}</p>,
    ul: ({ children }) => <ul className="my-3 list-disc space-y-1.5 pl-6 text-o-cuerpo leading-[1.6] text-oficina-texto2 marker:text-oficina-acento">{children}</ul>,
    ol: ({ children }) => <ol className="my-3 list-decimal space-y-1.5 pl-6 text-o-cuerpo leading-[1.6] text-oficina-texto2 marker:font-semibold marker:text-oficina-acento">{children}</ol>,
    strong: ({ children }) => <strong className="font-semibold text-oficina-texto">{children}</strong>,
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noreferrer" className="text-oficina-enlace underline decoration-oficina-enlace/40 underline-offset-2 hover:decoration-oficina-enlace">
        {children}
      </a>
    ),
    blockquote: ({ children }) => <blockquote className="my-4 border-l-2 border-oficina-acento pl-4 italic text-oficina-texto2">{children}</blockquote>,
    hr: () => <hr className="my-8 border-oficina-linea" />,
    table: ({ children }) => (
      <div className="my-5 overflow-x-auto rounded-o-l border border-oficina-linea">
        <table className="w-full border-collapse text-left text-o-texto">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-oficina-panel text-o-nota text-oficina-texto3">{children}</thead>,
    th: ({ children, style }) => <th style={style} className="px-4 py-2.5 font-semibold">{children}</th>,
    td: ({ children, style }) => <td style={style} className="border-t border-oficina-linea px-4 py-2.5 align-top text-oficina-texto2 [&_strong]:text-oficina-texto">{children}</td>,
    code: ({ children }) => <code className="rounded bg-oficina-elevada px-1.5 py-0.5 text-[0.9em] text-oficina-texto">{children}</code>,
    img: ({ src, alt }) => {
      const url = typeof src === 'string' ? imagen(src) : null;
      return (
        <span className="my-6 block">
          {url ? (
            <span className="block overflow-hidden rounded-o-l border border-oficina-linea [&_img]:max-h-none [&_img]:object-contain">
              <MiniaturaImagen src={url} nombre={alt || t('oficinaV2.informe.imagen')} />
            </span>
          ) : (
            <span className="block rounded-o-l border border-dashed border-oficina-linea px-4 py-6 text-center text-o-sub text-oficina-texto3">{t('oficinaV2.informe.imagenNoDisponible')}</span>
          )}
          {alt ? <span className="mt-2 block text-center text-o-sub text-oficina-texto3">{alt}</span> : null}
        </span>
      );
    },
    pre: ({ node, children }) => {
      const code = node?.children.find((c): c is Element => c.type === 'element' && c.tagName === 'code');
      const len = code ? lenguaje(code) : null;
      const fuente = code ? texto(code.children).replace(/\n$/, '') : '';
      if (len === 'cifras') return <Cifras fuente={fuente} />;
      if (len === 'opciones') return <Opciones fuente={fuente} imagen={imagen} />;
      if (len === 'grafica') {
        const g = leerGrafica(fuente);
        if (g) return <Grafica g={g} />;
      }
      if (len && len in AVISO) {
        const a = AVISO[len as Aviso];
        const Icono = a.icono;
        return (
          <div className={cn('my-6 rounded-o-l border px-5 py-4', a.clase)}>
            <div className={cn('mb-1 flex items-center gap-2 text-o-nota font-bold', a.tinta)}>
              <Icono className="h-4 w-4" aria-hidden />
              {t(a.clave)}
            </div>
            <div className="[&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p]:my-1.5 [&_p]:text-oficina-texto">{md(fuente, imagen)}</div>
          </div>
        );
      }
      return <pre className="my-4 overflow-x-auto rounded-o-m bg-oficina-elevada p-4 text-o-sub leading-[1.55] text-oficina-texto2 [&_code]:bg-transparent [&_code]:p-0">{children}</pre>;
    },
  };
}

/** Las imágenes guardadas («adjunto:<id>») pasan; el resto, con el filtro de siempre (fuera javascript: y compañía). */
const urlSegura = (url: string) => (/^adjunto:[\w-]+$/.test(url) ? url : defaultUrlTransform(url));

function md(fuente: string, imagen: (src: string) => string | null): ReactNode {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={componentes(imagen)} urlTransform={urlSegura}>
      {fuente}
    </ReactMarkdown>
  );
}

/**
 * Un informe entero. `imagen` traduce la ruta de una imagen (las del agente llegan como «adjunto:<id>», ver
 * backend/src/oficina/informe.ts) a su dirección en el panel, o null si no se puede enseñar.
 */
export function Informe({ fuente, imagen }: { fuente: string; imagen: (src: string) => string | null }) {
  return <article className="mx-auto w-full max-w-[860px] [&>:first-child]:mt-0 [&>h1+p]:text-o-titulo3 [&>h1+p]:leading-[1.55] [&>h1+p]:text-oficina-texto">{md(fuente, imagen)}</article>;
}
